import React from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import Image from '@v2/context/Image/context';
import Svg from '@styles/icons/svg';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { WithColor } from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import {
  TILE_HEIGHT_WEB,
  LINE_HEIGHT,
  IMAGE_WIDTH,
  IMAGE_HEIGHT,
  IMAGE_CONTAINER_WIDTH,
  IMAGE_CONTAINER_HEIGHT,
  IMAGE_CONTAINER_PADDING,
  HEADER_IMAGE_TEXT_GAP,
} from '@components/search/operations/constants';

import type { OperationSearchItem } from '@interfaces/search';
import type { ScaleFontProps } from '@components/search/operations/components/StartJobPopup/types';

import { useManageStartJobHeader } from './hooks/useManageStartJobHeader';
import HeaderColumn from './components/HeaderColumn';

export interface StartJobHeaderProps extends WithColor, ScaleFontProps {
  item: OperationSearchItem;
}

const StartJobHeader = ({ C, item, scale = initialScale, font = initialFont }: StartJobHeaderProps) => {
  const { imageData, operationName, machineName, operationNumber, ncFilesLabel, adherenceText, showBehindError, quantityTarget } =
    useManageStartJobHeader({ item, scale });

  const grayLine = [font.Body, { color: C('Label/Light/Tertiary') }];
  const boldLine = [font['Headline Large'], { color: C('Label/Light/Primary') }];
  const lineRow = { height: scale(LINE_HEIGHT), justifyContent: 'center' } as const;
  const lineRowInline = { height: scale(LINE_HEIGHT), flexDirection: 'row', alignItems: 'center' } as const;

  return (
    <View
      style={[
        getBoxShadowStyle({ type: 'Narrow', C }),
        {
          width: '100%',
          height: scale(TILE_HEIGHT_WEB),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          backgroundColor: C('Background/Light/Field'),
        },
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'stretch', paddingRight: scale(16), gap: scale(HEADER_IMAGE_TEXT_GAP) }}>
        {/* Part image */}
        <View style={{ width: scale(IMAGE_CONTAINER_WIDTH), height: scale(IMAGE_CONTAINER_HEIGHT), padding: scale(IMAGE_CONTAINER_PADDING) }}>
          <View
            testID={TEST_IDS.searchScreen.operations.infoTileImage}
            style={{ width: scale(IMAGE_WIDTH), height: scale(IMAGE_HEIGHT), borderRadius: scale(SCREEN_BORDER_RADIUS), overflow: 'hidden' }}
          >
            <Image {...imageData} />
          </View>
        </View>

        <View style={{ flex: 1, flexDirection: 'row' }}>
          {/* Column 1: operation type / machine / operation number */}
          <HeaderColumn scale={scale}>
            <View style={lineRow}>
              <Text numberOfLines={1} style={grayLine}>
                {operationName}
              </Text>
            </View>
            <View style={lineRowInline}>
              <Text numberOfLines={1} style={grayLine}>
                {machineName ? `${i18n.t('on')} ${machineName}` : i18n.t('Select Machine')}
              </Text>
            </View>
            <View style={lineRow}>
              <Text numberOfLines={1} style={boldLine}>
                {operationNumber}
              </Text>
            </View>
          </HeaderColumn>

          {/* Column 2: NC files / scheduled start / schedule adherence */}
          <HeaderColumn scale={scale}>
            <View style={lineRow}>
              <Text numberOfLines={1} style={grayLine}>
                {ncFilesLabel}
              </Text>
            </View>
            <View style={lineRow}>
              <Text numberOfLines={1} style={grayLine}>
                {i18n.t('Scheduled Start')}
              </Text>
            </View>
            <View style={[lineRowInline, { gap: scale(6) }]}>
              {showBehindError && <Svg icon="error" color="Label/Light/Warning" width={scale(16)} height={scale(14)} />}
              <Text numberOfLines={1} style={boldLine}>
                {adherenceText}
              </Text>
            </View>
          </HeaderColumn>

          {/* Column 3: pieces — two empty rows keep it aligned with the bold values on its left */}
          <View style={{ justifyContent: 'center' }}>
            <View style={{ height: scale(LINE_HEIGHT * 2) }} />
            <View style={{ height: scale(LINE_HEIGHT), justifyContent: 'center', alignItems: 'flex-end' }}>
              <ReadyPcsQuantity
                batch={null}
                quantityProduced={quantityTarget}
                C={C}
                scale={scale}
                font={font}
                fontForQuantityProduced="Time 1"
                colorForQuantityProduced="Label/Light/Tertiary"
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export default StartJobHeader;
